import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import AppShell from "./AppShell";
import { ROUTES, TAB_TO_PATH, DEFAULT_TAB } from "./routes";

/**
 * App — router + provider setup ONLY.
 *
 * The old 885-line god-component was decomposed into:
 *   - AppShell    — all global state, SSE/polling, scopedFetch, theme,
 *                   selected-agent scope, sidebar/topbar, command palette,
 *                   quick-ask. Mounted ONCE; renders <Outlet/>.
 *   - src/pages/* — one lazy page per route (React.lazy + Suspense in the
 *                   shell), each reading shared state from AppContext.
 *   - src/routes  — the single route table the nav/router/palette derive from.
 *
 * Auth is NOT handled here — main.tsx wraps <App/> in Firebase <AuthGate>.
 *
 * BrowserRouter basename: the SPA is served by FastAPI behind a base path.
 * When loaded under /next we set the basename so in-app links + history stay
 * scoped. /chat is handled inside the shell (chat-only mode short-circuits
 * the router chrome).
 */
function computeBasename(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return window.location.pathname.startsWith("/next") ? "/next" : undefined;
}

/**
 * Preserve legacy `?tab=X` deep links: any pre-router URL like `/next/?tab=files`
 * or `/?tab=social` gets redirected to the matching route on load. Runs only
 * when a `tab` query param is present AND we're at a root-ish path (so we don't
 * hijack a real route that happens to carry query state).
 */
function LegacyTabRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const tab = params.get("tab");
  const atRoot = location.pathname === "/" || location.pathname === "";
  if (tab && atRoot && TAB_TO_PATH[tab]) {
    return <Navigate to={`/${TAB_TO_PATH[tab]}`} replace />;
  }
  if (atRoot) {
    return <Navigate to={`/${TAB_TO_PATH[DEFAULT_TAB]}`} replace />;
  }
  return null;
}

export default function App() {
  return (
    <BrowserRouter basename={computeBasename()}>
      <Routes>
        <Route element={<AppShell />}>
          {/* index → resolve legacy ?tab= or fall through to default route */}
          <Route index element={<LegacyTabRedirect />} />
          {ROUTES.map((r) => {
            const Page = r.Component;
            return <Route key={r.id} path={r.path} element={<Page />} />;
          })}
          {/* Unknown in-app path → default route. */}
          <Route path="*" element={<Navigate to={`/${TAB_TO_PATH[DEFAULT_TAB]}`} replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
